import { Location } from "@angular/common";
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  HostListener,
  inject,
  Inject,
  signal,
} from "@angular/core";
import { ActivatedRoute, NavigationEnd, NavigationError, Router } from "@angular/router";

import markerSDK from "@marker.io/browser";
import { filter, pairwise, skip, startWith } from "rxjs";

import { BulkAddSlideOverModel } from "@components/shared/bulk-add-slide-over/bulk-add-slide-over.model";
import { BulkAddSlideOverService } from "@components/shared/bulk-add-slide-over/bulk-add-slide-over.service";
import { SlideOverlayPageService } from "@components/shared/overlay/slide-overlay-page/slide-overlay-page.service";
import { SidebarMenuService } from "@components/shared/sidebar/sidebar-menu/sidebar-menu.service";
import {
  AuthUserTypeEnum,
  EnvironmentEnum,
  InvitationStatusEnum,
  InboundShareStatusEnum,
  FeatureFlagEnum,
  RouteEnum,
} from "@shared/enums";
import { IAvailableOrganisation, IConfig, IUser, IUserData } from "@shared/interfaces";
import {
  AuthenticationService,
  CommonService,
  DownloadDocumentsService,
  ErrorReportingService,
  FeatureFlagService,
  GoogleAnalyticsService,
  GoogleTagManagerService,
  InvitationsService,
  RecordSharingService,
  UsersService,
} from "@shared/services";
import { BatchActionService } from "@shared/services/batch-action.service";
import { RouterService } from "@shared/services/router.service";
import { APP_CONFIG } from "@shared/tokens";

@Component({
  standalone: false,
  selector: "app-root",
  templateUrl: "./app.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements AfterViewInit {
  @HostListener("window:beforeunload")
  canDeactivate(): boolean {
    return (
      !this.downloadDocumentsService.isDownloadingDocuments() &&
      !this.batchActionService.isInProgress()
    );
  }

  private readonly route = inject(ActivatedRoute);

  private readonly router = inject(Router);

  private readonly authenticationService = inject(AuthenticationService);

  private readonly commonService = inject(CommonService);

  private readonly invitationsService = inject(InvitationsService);

  private readonly usersService = inject(UsersService);

  private readonly overlay = inject(SlideOverlayPageService);

  private readonly bulkAddService = inject(BulkAddSlideOverService);

  private readonly downloadDocumentsService = inject(DownloadDocumentsService);

  private readonly batchActionService = inject(BatchActionService);

  private readonly featureFlagService = inject(FeatureFlagService);

  private readonly recordSharingService = inject(RecordSharingService);

  private readonly routerService = inject(RouterService);

  private readonly errorReportingService = inject(ErrorReportingService);

  private readonly sidebarMenuService = inject(SidebarMenuService);

  private readonly googleAnalyticsService = inject(GoogleAnalyticsService);

  private readonly googleTagManagerService = inject(GoogleTagManagerService);

  private readonly location = inject(Location);

  readonly environmentName: string = this.environment.name;

  isLoggedIn = signal(!this.authenticationService.isLocked());

  isShowMenuAndHeaderRoute = signal(true);

  isShowMenuAndHeader = computed(
    () => this.isLoggedIn() && this.userData() && this.isShowMenuAndHeaderRoute(),
  );

  userData = signal<IUserData>(undefined);

  activeOrganisation: IAvailableOrganisation = null;

  pendingInvitationsCount = signal<number>(0);

  newInboundSharesCount = signal<number>(0);

  userName: string = null;

  userType: AuthUserTypeEnum;

  isCrossOrgSharingEnabled = this.featureFlagService.isEnabled(
    FeatureFlagEnum.CROSS_ORGANISATION_SHARING,
  );

  public isPurchaseOrdersEnabled = this.featureFlagService.isEnabled(
    FeatureFlagEnum.PURCHASE_ORDERS,
  );

  isAccountOwnerOrAdminOrContributor =
    this.authenticationService.isAccountOwnerOrAdminOrContributor();

  canAccessAdmin = this.authenticationService.canAccessAdmin;

  hasEntitiesWriteAccess = this.authenticationService.hasEntitiesWriteAccess;

  isSharedUser = this.authenticationService.isSharedUser();

  constructor(@Inject(APP_CONFIG) private environment: IConfig) {
    this.googleTagManagerService.init();
    console.log(`Environment: ${this.environmentName}`);

    effect(() => {
      if (this.isLoggedIn()) {
        this.commonService.loadSystemUnitsOfMeasurements();
        this.commonService.loadCountries();
      }
    });

    this.isShowMenuAndHeaderRoute.set(this.getIsShowMenuAndHeaderRoute());

    this.setupSubscriptions();
  }

  async ngAfterViewInit(): Promise<void> {
    this.route.queryParams
      .pipe(skip(1), startWith(null), pairwise())
      .subscribe(([previousParams, params]) => {
        if (this.overlay.canDeactivateInProgress()) {
          this.overlay.canDeactivateInProgress.set(false);

          return;
        }

        if (this.bulkAddService.canDeactivateInProgress()) {
          this.bulkAddService.canDeactivateInProgress.set(false);

          return;
        }

        this.overlay.toggle(params["view"], params["id"]);

        if (!previousParams || params["bulkAdd"] !== previousParams["bulkAdd"]) {
          this.bulkAddService.toggle(params["bulkAdd"] as BulkAddSlideOverModel.ResourceTypeEnum);
        }
      });

    if (this.environmentName === EnvironmentEnum.UAT && this.environment.marketProjectId) {
      await markerSDK.loadWidget({
        project: this.environment.marketProjectId,
        source: "snippet",
      });
    }
  }

  private onUserDataChanged = (userData: IUserData): void => {
    this.activeOrganisation = null;
    this.pendingInvitationsCount.set(0);
    this.newInboundSharesCount.set(0);
    this.userName = null;
    this.userData.set(userData);

    if (userData) {
      this.isCrossOrgSharingEnabled = this.featureFlagService.isEnabled(
        FeatureFlagEnum.CROSS_ORGANISATION_SHARING,
      );

      this.isPurchaseOrdersEnabled = this.featureFlagService.isEnabled(
        FeatureFlagEnum.PURCHASE_ORDERS,
      );

      this.isAccountOwnerOrAdminOrContributor =
        this.authenticationService.isAccountOwnerOrAdminOrContributor();
      this.canAccessAdmin = this.authenticationService.canAccessAdmin;
      this.hasEntitiesWriteAccess = this.authenticationService.hasEntitiesWriteAccess;
      this.isSharedUser = this.authenticationService.isSharedUser();
      this.userType = this.authenticationService.getUserType();
      switch (this.userType) {
        case AuthUserTypeEnum.REGULAR:
          this.invitationsService.getPage(userData.email, undefined, InvitationStatusEnum.PENDING);
          this.usersService.get(userData.id).then((user: IUser) => {
            this.userName = user ? `${user.firstName} ${user.lastName}` : "";
          });
          if (this.isCrossOrgSharingEnabled && this.isAccountOwnerOrAdminOrContributor) {
            this.recordSharingService.getAllInboundShares(InboundShareStatusEnum.NEW);
          }
          if (userData.availableOrganisations?.length) {
            if (userData.activeOrganisationIndex !== null) {
              this.activeOrganisation =
                userData.availableOrganisations[userData.activeOrganisationIndex];
            } else {
              this.activeOrganisation = userData.availableOrganisations[0];
            }
          }
          break;
      }
    }
    this.errorReportingService.setUserContext(userData);
  };

  private getIsShowMenuAndHeaderRoute = (): boolean => {
    const currentUrl = this.location.path();

    return !(
      currentUrl.startsWith(`/${RouteEnum.DOCUMENTS_VIEW}`) ||
      currentUrl.startsWith(`/${RouteEnum.ORGANISATIONS_ADD}`)
    );
  };

  private setupSubscriptions = (): void => {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd || event instanceof NavigationError))
      .subscribe((event: any) => {
        switch (true) {
          case event instanceof NavigationEnd:
            {
              this.sidebarMenuService.currentUrl.set(this.router.routerState.snapshot.url);
              this.isShowMenuAndHeaderRoute.set(this.getIsShowMenuAndHeaderRoute());

              this.isLoggedIn.set(!this.authenticationService.isLocked());

              if (this.isLoggedIn()) {
                this.routerService.addHistory(this.sidebarMenuService.currentUrl());
              } else {
                this.routerService.clearHistory();
              }

              this.googleAnalyticsService.trackPageView(this.router.routerState.snapshot.url);
            }
            break;
          case event instanceof NavigationError:
            {
              // Logic for reloading app when trying to fetch a lazy load module
              // This error happens when a new release has taken place (producing diffrent module filenames)
              // And so a now old version of the app is already running and trying to fetch an old (already deleted) module reference
              // This will force a refresh of the route, hence fetching a new index.html file with all the new app references.
              console.error(`Navigation error. Reloading: ${event.url}`, event);
              window.location.assign(event.url);
            }
            break;
        }
      });

    this.authenticationService.userDataObservable$.subscribe((userData: IUserData) => {
      this.onUserDataChanged(userData);
    });

    this.invitationsService.pendingInvitationsCountObservable$.subscribe(
      (pendingInvitationsCount: number) => {
        this.pendingInvitationsCount.set(pendingInvitationsCount);
      },
    );

    this.recordSharingService.newInboundSharesCountObservable$.subscribe(
      (newInboundSharesCount: number) => {
        this.newInboundSharesCount.set(newInboundSharesCount);
      },
    );
  };
}
